/**
 * Шапка раздела «Планы»: заголовок с итогами отбора и четыре плитки чисел.
 * Вынесено из `pages/PlansPage.tsx` (нарезка 14.10.26).
 */

import { nf } from "@/lib/format";
import type { PlanTotals } from "@/lib/providers";

export function PlansHeader({ totals, isFetching }: { totals: PlanTotals | undefined; isFetching: boolean }) {
  return (
    <>
      <div className="mb-4">
        <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
          Планы <span className="font-normal text-fg-faint">на фабрику</span>
        </h1>
        {/* Пока итогов нет — прочерки, не нули: «0 партий-дат» на первом
            заходе читалось бы как пустой отбор. */}
        <div className="text-[13px] text-fg-muted">
          <b className="tnum font-medium text-fg">
            {totals ? nf.format(totals.plan_count) : "—"}
          </b>{" "}
          партий-дат в{" "}
          <b className="tnum font-medium text-fg">{totals ? totals.group_count : "—"}</b>{" "}
          группах · без QR-папки{" "}
          <b className="tnum font-medium text-fg">{totals ? totals.without_qr : "—"}</b> ·
          не оплачено{" "}
          <b className="tnum font-medium text-fg">{totals ? totals.unpaid : "—"}</b> · без
          карты{" "}
          <b className="tnum font-medium text-fg">{totals ? totals.without_card : "—"}</b>
          {isFetching ? " · обновляем…" : ""}
        </div>
      </div>

      {/* Те же четыре числа, что в сводке старого экрана. Набор не выдуман —
          по ним закупку и проверяют. */}
      {/* Четыре плитки в ряд на 390px дают колонку в 80px, где «штук по
          текущему отбору» переносится в шесть строк. На телефоне — две. */}
      <section className="mb-4 grid flex-none grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="План"
          value={totals ? nf.format(totals.qty_planned) : undefined}
          note="штук по текущему отбору"
        />
        <Stat
          label="В заказе"
          value={totals ? nf.format(totals.qty_ordered) : undefined}
          note={
            totals && totals.qty_planned > 0
              ? `${((totals.qty_ordered / totals.qty_planned) * 100).toFixed(1)}% от плана`
              : "—"
          }
        />
        <Stat
          label="Сумма"
          value={
            totals ? nf.format(Math.round(Number(totals.amount))) : undefined
          }
          note="юаней по закупочной цене"
        />
        <Stat
          label="Нет цены"
          value={totals ? nf.format(totals.no_price_lines) : undefined}
          note="строк не уедут в заказ"
          alarm={(totals?.no_price_lines ?? 0) > 0}
        />
      </section>
    </>
  );
}

function Stat({
  label,
  value,
  note,
  alarm,
}: {
  label: string;
  value: string | undefined;
  note: string;
  alarm?: boolean;
}) {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-4 py-3">
      <div className="mb-1 text-[11.5px] text-fg-muted">{label}</div>
      <div
        className={
          "tnum text-[21px] font-medium tracking-[-0.02em] " +
          (alarm ? "text-err" : "")
        }
      >
        {value ?? "—"}
      </div>
      <div className="mt-0.5 text-[11.5px] text-fg-faint">{note}</div>
    </div>
  );
}
